import { useEffect, useState } from 'react';
import { VehiclePlan } from '../types/vehicle';
import { useSubmit } from 'react-router';

export const findPlanIdByMileage = (
  vehiclePlans: VehiclePlan[],
  mileage?: number
): string | undefined => {
  return vehiclePlans?.find((plan) => plan.miles_per_month === mileage)?.id?.toString();
};

/**
 * Hook to organize the logic for handling the selection of a plan in the PDP.
 * @param vehiclePlans plans of vehicle
 * @param selectedMileagePlan Number of miles per month for plan
 * @param postSubmitDelegate delegate function to call after submit occurs to react-router
 * @returns
 */
export default function usePDPSelectPlanSubmit(
  vehiclePlans: VehiclePlan[],
  selectedMileagePlan?: number,
  postSubmitDelegate?: (event: string) => void
): {
  handleSelectPlan: (event: string) => void;
  selectedPlanId?: string;
} {
  const submit = useSubmit();

  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(
    findPlanIdByMileage(vehiclePlans, selectedMileagePlan)
  );

  // When the selectedMileagePlan changes, update the selectedPlanId to effectively reset the state of
  // selectedPlanId to the initial state. This is necessary for scenarios where the selectedMileagePlan
  // gets changed outside of the handleSelectPlan function and we need to keep the session cart value and
  // selected plan in the UI in sync.
  useEffect(() => {
    setSelectedPlanId(findPlanIdByMileage(vehiclePlans, selectedMileagePlan));
  }, [selectedMileagePlan, vehiclePlans]);

  const handleSelectPlan = (event: string) => {
    const selectedPlanMileage = vehiclePlans.find(
      (plan) => plan.id === parseInt(event)
    )?.miles_per_month;

    if (selectedPlanMileage !== undefined) {
      submit(
        {
          intent: 'update_selected_plan_mileage',
          plan_mileage: selectedPlanMileage,
        },
        {
          method: 'post',
        }
      );
      setSelectedPlanId(event);
      postSubmitDelegate?.(event);
    }
  };

  return { handleSelectPlan, selectedPlanId };
}
